import { useState, useEffect } from 'react';
import { X, Clock } from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

// Animations CSS personnalisées
const customStyles = `
  @keyframes slideInFromRight {
    from {
      opacity: 0;
      transform: translateX(20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0px);
    }
    50% {
      transform: translateY(-6px);
    }
  }
  
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }
  
  .animate-slide-in-right {
    animation: slideInFromRight 0.5s ease-out forwards;
  }
  
  .animate-float {
    animation: float 3s ease-in-out infinite;
  }
  
  .shimmer {
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent);
    background-size: 1000px 100%;
    animation: shimmer 2s infinite;
  }
  
  .glass-morphism {
    background: rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.18);
  }
  
  .scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
  
  .scrollbar-hide::-webkit-scrollbar {
    display: none;
  }
`;

interface PRIsMEOverviewModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function PRIsMEOverviewModal({ isOpen, onClose }: PRIsMEOverviewModalProps) {
  const [activeTab, setActiveTab] = useState('overview');
  const [contentKey, setContentKey] = useState(0);
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['prisme_overview_last_ai_analysis'] ?? null) : null;

  // Injecter les styles CSS personnalisés
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = customStyles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  // Animation de transition entre onglets
  useEffect(() => {
    setContentKey(prev => prev + 1);
  }, [activeTab]);

  if (!isOpen) return null;

  const tabs = [
    {
      id: 'overview',
      label: 'Vue d\'ensemble',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      id: 'requirements',
      label: "Spécifications d'exigences",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      )
    },
    {
      id: 'architecture',
      label: 'Architecture technique',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      )
    },
    {
      id: 'cybersecurity',
      label: 'Cybersécurité NIST',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      id: 'kpi',
      label: 'Indicateurs KPI',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      )
    },
    {
      id: 'roi',
      label: '$ ROI & Payback',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      id: 'governance',
      label: 'Gouvernance & Conformité',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16l3-9" />
        </svg>
      )
    },
    {
      id: 'ai-integration',
      label: 'Intelligence Artificielle',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      )
    },
    {
      id: 'roadmap',
      label: 'Roadmap & Vision',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      )
    },
    {
      id: 'guide',
      label: 'Guide Utilisateur',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      )
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-8">
            {/* Hero Section PRO */}
            <div className="relative bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-600 rounded-3xl p-10 text-white overflow-hidden">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -mr-48 -mt-48"></div>
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/5 rounded-full -ml-48 -mb-48"></div>
              <div className="relative z-10 text-center">
                <div className="w-32 h-32 mx-auto mb-6 bg-white/20 rounded-full flex items-center justify-center animate-float shadow-2xl ring-8 ring-white/30 backdrop-blur-lg">
                  <svg className="w-16 h-16 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                </div>
                <h3 className="text-4xl font-black mb-4 tracking-tight">PRIsME</h3>
                <p className="text-2xl font-bold mb-3 text-blue-100">Intelligence Augmentée pour l'Excellence Organisationnelle</p>
                <p className="text-xl text-blue-50 max-w-3xl mx-auto leading-relaxed">
                  Plateforme holistique de développement de l'excellence chez BZ Inc. - De la Direction TI aux Ventes et Administration/RH
                </p>
                
                {/* Badges PRO */}
                <div className="flex items-center justify-center gap-4 mt-8">
                  <div className="bg-white/20 backdrop-blur-md px-6 py-3 rounded-full border border-white/30 flex items-center gap-2">
                    <span className="text-3xl">🏆</span>
                    <span className="font-bold text-lg">Excellence BZ</span>
                  </div>
                  <div className="bg-white/20 backdrop-blur-md px-6 py-3 rounded-full border border-white/30 flex items-center gap-2">
                    <span className="text-3xl">🤖</span>
                    <span className="font-bold text-lg">IA Collaborative</span>
                  </div>
                  <div className="bg-white/20 backdrop-blur-md px-6 py-3 rounded-full border border-white/30 flex items-center gap-2">
                    <span className="text-3xl">🎯</span>
                    <span className="font-bold text-lg">3 Départements</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistiques PRO */}
            <div className="grid grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border-2 border-blue-200 text-center transform hover:scale-105 transition-all duration-300 shadow-lg">
                <div className="text-5xl font-black text-blue-600 mb-2">54</div>
                <div className="text-sm font-bold text-blue-800 uppercase tracking-wide">Tuiles d'Excellence</div>
                <div className="text-xs text-blue-600 mt-1">3 départements × 18 piliers</div>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border-2 border-purple-200 text-center transform hover:scale-105 transition-all duration-300 shadow-lg">
                <div className="text-5xl font-black text-purple-600 mb-2">3</div>
                <div className="text-sm font-bold text-purple-800 uppercase tracking-wide">Experts BZ</div>
                <div className="text-xs text-purple-600 mt-1">Visions complémentaires</div>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-2xl border-2 border-orange-200 text-center transform hover:scale-105 transition-all duration-300 shadow-lg">
                <div className="text-5xl font-black text-orange-600 mb-2">162</div>
                <div className="text-sm font-bold text-orange-800 uppercase tracking-wide">Visions Totales</div>
                <div className="text-xs text-orange-600 mt-1">54 tuiles × 3 experts</div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border-2 border-green-200 text-center transform hover:scale-105 transition-all duration-300 shadow-lg">
                <div className="text-5xl font-black text-green-600 mb-2">100%</div>
                <div className="text-sm font-bold text-green-800 uppercase tracking-wide">Excellence</div>
                <div className="text-xs text-green-600 mt-1">Non-négociable</div>
              </div>
            </div>

            {/* PRIsME: Plateforme Pivot de Communication - SECTION STRATÉGIQUE */}
            <div className="relative bg-gradient-to-r from-indigo-600 via-blue-600 to-cyan-600 text-white p-10 rounded-3xl shadow-2xl border-4 border-indigo-200 overflow-hidden">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -mr-48 -mt-48"></div>
              <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full -ml-48 -mb-48"></div>
              
              <div className="relative z-10">
                <div className="text-center mb-8">
                  <div className="flex items-center justify-center gap-4 mb-4">
                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <h4 className="text-3xl font-black">PRIsME : Plateforme Pivot de Communication</h4>
                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                    </svg>
                  </div>
                  <p className="text-2xl font-bold text-cyan-100 mb-3">
                    PRIsME n'est pas qu'un outil interne. C'est le HUB central de communication, de transparence et d'excellence.
                  </p>
                  <p className="text-xl text-white/90">
                    Une plateforme unique qui connecte nos équipes internes ET nos clients via des espaces dédiés sécurisés.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  {/* Communication Interne */}
                  <div className="bg-white/20 backdrop-blur-md p-6 rounded-2xl border-2 border-white/30">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-16 h-16 bg-white/30 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <h5 className="text-2xl font-bold">Communication Interne</h5>
                    </div>
                    <ul className="space-y-3 text-white/95">
                      <li className="flex items-start gap-2">
                        <span className="text-xl">✓</span>
                        <div><strong>Standards Unifiés:</strong> Tous les départements BZ alignés sur les mêmes piliers d'excellence</div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-xl">✓</span>
                        <div><strong>Formation Continue:</strong> Onboarding et montée en compétences avec les visions des experts</div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-xl">✓</span>
                        <div><strong>Culture Partagée:</strong> Valeurs, philosophies et méthodologies accessibles 24/7</div>
                      </li>
                    </ul>
                  </div>

                  {/* Communication Externe - Espaces Clients */}
                  <div className="bg-white/20 backdrop-blur-md p-6 rounded-2xl border-2 border-white/30">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-16 h-16 bg-white/30 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h5 className="text-2xl font-bold">Espaces Clients Privés</h5>
                    </div>
                    <ul className="space-y-3 text-white/95">
                      <li className="flex items-start gap-2">
                        <span className="text-xl">🔐</span>
                        <div><strong>Portails Dédiés:</strong> Chaque client a son espace PRIsME personnalisé et sécurisé</div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-xl">📊</span>
                        <div><strong>Transparence Totale:</strong> Standards appliqués, KPIs, livrables, méthodologies visibles</div>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-xl">🤝</span>
                        <div><strong>Collaboration BZ-Client:</strong> Partage de connaissances et co-création de valeur</div>
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Vision Futur - Roadmap PRIsME Client */}
                <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border-2 border-white/40">
                  <div className="text-center mb-6">
                    <h5 className="text-2xl font-black mb-3 flex items-center justify-center gap-3">
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      Vision Future: PRIsME comme Différenciateur Compétitif Majeur
                      <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </h5>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4 text-center">
                    <div className="bg-white/20 p-5 rounded-xl">
                      <div className="text-4xl font-black mb-2">2025 Q2</div>
                      <div className="font-bold mb-2">Lancement Espaces Clients</div>
                      <div className="text-sm text-white/90">Portails PRIsME personnalisés pour clients Premium</div>
                    </div>
                    <div className="bg-white/20 p-5 rounded-xl">
                      <div className="text-4xl font-black mb-2">2025 Q3</div>
                      <div className="font-bold mb-2">Dashboards Interactifs</div>
                      <div className="text-sm text-white/90">Métriques live, KPIs partagés, rapports automatisés</div>
                    </div>
                    <div className="bg-white/20 p-5 rounded-xl">
                      <div className="text-4xl font-black mb-2">2025 Q4</div>
                      <div className="font-bold mb-2">Certification BZ Standards</div>
                      <div className="text-sm text-white/90">Clients certifiés BZ Excellence via PRIsME</div>
                    </div>
                  </div>

                  <div className="mt-6 text-center">
                    <p className="text-lg font-bold italic">
                      "Quand nos clients voient la rigueur et la transparence de nos standards via PRIsME, ils comprennent pourquoi BZ est différent. 
                      La transparence devient notre arme atomique commerciale." - Dany Bourassa 🧡
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="space-y-4">
                <h4 className="text-xl font-semibold text-gray-900">🎯 Vision & Mission</h4>
                <p className="text-gray-700">
                  PRIsME centralise l'ensemble des standards d'excellence BZ dans une plateforme unifiée. 
                  Chaque département (TI, Ventes, Admin/RH) dispose de ses 18 piliers d'excellence avec les visions 
                  complémentaires de nos 3 experts : Dany 🧡, Dany ❤️, et François 💙.
                </p>
                
                <h4 className="text-xl font-semibold text-gray-900 mt-6">🏢 Départements Intégrés</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <strong>Direction TI:</strong> Excellence technologique et solutions d'affaires
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <strong>Directeur de Compte:</strong> Développement commercial et croissance durable
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                    <strong>Administration & RH:</strong> Excellence humaine et opérationnelle
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                    <strong>Concepts:</strong> Cadres méthodologiques et frameworks
                  </li>
                </ul>
              </div>

              <div className="space-y-4">
                <h4 className="text-xl font-semibold text-gray-900">👥 Les 3 Experts</h4>
                <p className="text-gray-700">
                  Chaque concept PRIsME est enrichi par les perspectives complémentaires de nos trois leaders :
                </p>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-center gap-2">
                    <span className="text-2xl">🧡</span>
                    <div>
                      <strong>Dany - Président:</strong> Vision stratégique et leadership holistique
                    </div>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-2xl">❤️</span>
                    <div>
                      <strong>Dany - CA/CE:</strong> Développement d'affaires et croissance
                    </div>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-2xl">💙</span>
                    <div>
                      <strong>François - VP Techno:</strong> Excellence technique et innovation
                    </div>
                  </li>
                </ul>

                <div className="relative overflow-hidden bg-gradient-to-r from-blue-50 to-cyan-50 p-4 rounded-lg border border-blue-200 group">
                  <div className="absolute inset-0 shimmer opacity-0 group-hover:opacity-100"></div>
                  <h5 className="font-semibold text-blue-900 mb-2 relative z-10">🎯 Objectif Principal</h5>
                  <p className="text-blue-800 text-sm relative z-10">
                    Démocratiser l'excellence organisationnelle en rendant accessibles les standards BZ 
                    à tous les niveaux de l'entreprise, avec une vision holistique et collaborative.
                  </p>
                </div>
              </div>
            </div>

            {/* Concepts fondamentaux + Standards BZ (fusionnés) */}
            <div className="grid md:grid-cols-2 gap-6 mt-6">
              <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-md">
                <h4 className="text-xl font-bold text-gray-900 mb-4">🔑 Concepts fondamentaux (référencés)</h4>
                <ul className="space-y-3 text-gray-700 text-sm">
                  <li><strong>Arbre Tronc–Branches–Feuilles</strong> — décomposition des systèmes (Réf. Donella Meadows, Thinking in Systems)</li>
                  <li><strong>Cascade stratégique-tactique (Hoshin/OKR/TactiCE)</strong> — alignement Vision → objectifs → actions (Réf. Akao, Doerr, PMI Portfolio)</li>
                  <li><strong>RACI / Gouvernance claire</strong> — responsabilité explicite sur prismes employés/clients (Réf. PMBOK, ITIL 4)</li>
                  <li><strong>Niveaux de maturité 1→5</strong> — qualité graduelle des livrables (Réf. CMMI, COBIT, NIST CSF Tiers)</li>
                  <li><strong>Prisme Master → Prismes dérivés</strong> — jumeau de connaissance, synchronisation + feedback (Réf. SECI Nonaka/Takeuchi, Digital Twin of Organization)</li>
                  <li><strong>Boucles d’apprentissage rapides</strong> — Build-Measure-Learn, Kaizen/PDCA (Réf. Lean Startup, Deming)</li>
                  <li><strong>Flow visuel & décisionnel</strong> — données → graphiques → décision/action (Réf. Tufte, Data-Driven Decision Making)</li>
                  <li><strong>Modularité / réutilisation</strong> — design system, blocs gabarits (Réf. Design Systems, Component-based Architecture)</li>
                  <li><strong>Sécurité & conformité by design</strong> — intégrée dès la conception (Réf. NIST CSF, ISO 27001, COBIT, Privacy by Design)</li>
                  <li><strong>Mesure d’adoption & de valeur</strong> — usage, satisfaction, impact business (Réf. Product Analytics AARRR / North Star, ITIL CSI)</li>
                </ul>
              </div>
              <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-md">
                <h4 className="text-xl font-bold text-gray-900 mb-4">🏛️ Standards BZ (adossés aux concepts)</h4>
                <ul className="space-y-3 text-gray-700 text-sm">
                  <li><strong>ITIL 4 / COBIT 2019</strong> — services, gouvernance TI, rôles & RACI</li>
                  <li><strong>NIST CSF 2.0</strong> — gestion des risques, cybersécurité, niveaux de maturité</li>
                  <li><strong>ISO 27001</strong> — SMSI, contrôle et amélioration continue sécurité</li>
                  <li><strong>PMBOK / Hoshin / OKR</strong> — portefeuille, programmes, objectifs alignés</li>
                  <li><strong>NIST AI RMF</strong> — principes IA (sécurité, transparence, équité, explicabilité)</li>
                  <li><strong>Privacy by Design / Loi 25</strong> — protection des données dès la conception</li>
                  <li><strong>Lean / Kaizen</strong> — amélioration continue et boucles courtes</li>
                  <li><strong>Design Systems</strong> — modularité et réutilisation des gabarits PRIsME</li>
                </ul>
              </div>
            </div>

            {/* Citations des 3 Experts - PRO TOUCH */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* Quote Dany */}
              <div className="relative bg-gradient-to-br from-orange-100 to-orange-50 p-6 rounded-3xl border-2 border-orange-300 shadow-xl transform hover:scale-105 transition-all duration-300">
                <div className="absolute top-3 left-4 text-orange-300 text-5xl font-serif">"</div>
                <div className="absolute bottom-3 right-4 text-orange-300 text-5xl font-serif">"</div>
                <div className="relative z-10 pt-8 pb-4 px-4">
                  <p className="text-lg font-bold text-gray-800 leading-relaxed mb-6 italic text-center">
                    Excellence is not negotiable.
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-14 h-14 rounded-full ring-3 ring-orange-400 overflow-hidden shadow-lg">
                      <img src="/DB.png" alt="Dany Bourassa" className="w-full h-full object-cover" />
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-gray-900">Dany 🧡</div>
                      <div className="text-xs text-gray-600">Président</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quote Dany */}
              <div className="relative bg-gradient-to-br from-red-100 to-red-50 p-6 rounded-3xl border-2 border-red-300 shadow-xl transform hover:scale-105 transition-all duration-300">
                <div className="absolute top-3 left-4 text-red-300 text-5xl font-serif">"</div>
                <div className="absolute bottom-3 right-4 text-red-300 text-5xl font-serif">"</div>
                <div className="relative z-10 pt-8 pb-4 px-4">
                  <p className="text-lg font-bold text-gray-800 leading-relaxed mb-6 italic text-center">
                    La croissance durable naît de l'équilibre entre ambition stratégique et valeurs humaines.
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-14 h-14 rounded-full ring-3 ring-red-400 overflow-hidden shadow-lg">
                      <img src="/DB.png" alt="Dany" className="w-full h-full object-cover" />
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-gray-900">Dany ❤️</div>
                      <div className="text-xs text-gray-600">CA/CE</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quote François */}
              <div className="relative bg-gradient-to-br from-blue-100 to-blue-50 p-6 rounded-3xl border-2 border-blue-300 shadow-xl transform hover:scale-105 transition-all duration-300">
                <div className="absolute top-3 left-4 text-blue-300 text-5xl font-serif">"</div>
                <div className="absolute bottom-3 right-4 text-blue-300 text-5xl font-serif">"</div>
                <div className="relative z-10 pt-8 pb-4 px-4">
                  <p className="text-lg font-bold text-gray-800 leading-relaxed mb-6 italic text-center">
                    L'innovation technique sans vision humaine n'est que du code. L'alliance des deux crée l'impact.
                  </p>
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-14 h-14 rounded-full ring-3 ring-blue-400 overflow-hidden shadow-lg">
                      <img src="/FP.png" alt="François" className="w-full h-full object-cover" />
                    </div>
                    <div className="text-left">
                      <div className="font-bold text-gray-900">François 💙</div>
                      <div className="text-xs text-gray-600">VP Techno</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'requirements':
        return (
          <div className="space-y-8">
            {/* Header PRO */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center animate-float shadow-xl ring-4 ring-blue-200">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">Spécifications d'exigences d'affaires et TI PRIsME</h3>
              <p className="text-gray-600 text-lg">Spécifications fonctionnelles et techniques complètes</p>
            </div>

            {/* Vision et Objectifs */}
            <div className="bg-gradient-to-r from-blue-100 via-indigo-100 to-purple-100 p-8 rounded-3xl border-2 border-blue-300 shadow-xl">
              <h4 className="text-2xl font-bold text-blue-900 mb-4 text-center flex items-center justify-center gap-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Vision et Objectifs du Projet
              </h4>
              <p className="text-blue-800 text-center mb-6">
                PRIsME est la plateforme centralisée d'excellence organisationnelle de BZ Inc.. Elle vise à standardiser, 
                documenter et perpétuer les meilleures pratiques à travers tous les départements de l'entreprise.
              </p>
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white p-5 rounded-2xl shadow-md text-center border-2 border-blue-200">
                  <div className="text-4xl font-black text-blue-600 mb-2">3</div>
                  <div className="font-bold text-blue-900">Départements</div>
                  <div className="text-sm text-blue-700">TI, Ventes, Admin/RH</div>
                </div>
                <div className="bg-white p-5 rounded-2xl shadow-md text-center border-2 border-indigo-200">
                  <div className="text-4xl font-black text-indigo-600 mb-2">54</div>
                  <div className="font-bold text-indigo-900">Tuiles d'Excellence</div>
                  <div className="text-sm text-indigo-700">18 par département</div>
                </div>
                <div className="bg-white p-5 rounded-2xl shadow-md text-center border-2 border-purple-200">
                  <div className="text-4xl font-black text-purple-600 mb-2">162</div>
                  <div className="font-bold text-purple-900">Visions Expertes</div>
                  <div className="text-sm text-purple-700">3 experts × 54 tuiles</div>
                </div>
              </div>
            </div>

            {/* Exigences Fonctionnelles */}
            <div className="bg-white p-8 rounded-2xl border-2 border-blue-200 shadow-lg">
              <h4 className="text-2xl font-bold text-blue-900 mb-6 flex items-center gap-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
                Exigences Fonctionnelles
              </h4>

              <div className="space-y-6">
                <div className="border-l-4 border-blue-500 pl-6">
                  <h5 className="font-bold text-blue-900 mb-3 text-lg">RF-001: Navigation Multi-Départements</h5>
                  <p className="text-gray-700 mb-2"><strong>Description:</strong> L'utilisateur doit pouvoir naviguer facilement entre les 3 départements (TI, Ventes, Admin/RH) via la sidebar gauche.</p>
                  <p className="text-gray-700 mb-2"><strong>Priorité:</strong> <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm font-bold">CRITIQUE</span></p>
                  <p className="text-gray-700"><strong>Critères d'acceptation:</strong> Changement de département en moins de 500ms, highlight visuel de la section active</p>
                </div>

                <div className="border-l-4 border-indigo-500 pl-6">
                  <h5 className="font-bold text-indigo-900 mb-3 text-lg">RF-002: Affichage des Tuiles d'Excellence</h5>
                  <p className="text-gray-700 mb-2"><strong>Description:</strong> Chaque département affiche 18 tuiles interactives avec titre, catégorie, description, KPI et avatars des 3 experts.</p>
                  <p className="text-gray-700 mb-2"><strong>Priorité:</strong> <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm font-bold">CRITIQUE</span></p>
                  <p className="text-gray-700"><strong>Critères d'acceptation:</strong> Grid responsive (1/2/3 colonnes selon écran), animations smooth, hover effects</p>
                </div>

                <div className="border-l-4 border-purple-500 pl-6">
                  <h5 className="font-bold text-purple-900 mb-3 text-lg">RF-003: Visions Expertes Interactives</h5>
                  <p className="text-gray-700 mb-2"><strong>Description:</strong> Cliquer sur un avatar d'expert (Dany, Dany, François) ouvre une modal avec sa vision détaillée du concept.</p>
                  <p className="text-gray-700 mb-2"><strong>Priorité:</strong> <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm font-bold">HAUTE</span></p>
                  <p className="text-gray-700"><strong>Critères d'acceptation:</strong> Modal fullscreen avec scrolling, fermeture par X ou ESC, transition animée</p>
                </div>

                <div className="border-l-4 border-green-500 pl-6">
                  <h5 className="font-bold text-green-900 mb-3 text-lg">RF-004: Système de Scoring par Expert</h5>
                  <p className="text-gray-700 mb-2"><strong>Description:</strong> Chaque expert attribue un score (étoiles /5) reflétant l'importance ou la profondeur du concept.</p>
                  <p className="text-gray-700 mb-2"><strong>Priorité:</strong> <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-sm font-bold">MOYENNE</span></p>
                  <p className="text-gray-700"><strong>Critères d'acceptation:</strong> Affichage visuel clair (étoiles), cohérence des scores entre experts</p>
                </div>

                <div className="border-l-4 border-blue-500 pl-6">
                  <h5 className="font-bold text-blue-900 mb-3 text-lg">RF-005: Page Accueil Globale</h5>
                  <p className="text-gray-700 mb-2"><strong>Description:</strong> Page d'accueil présentant PRIsME, les 3 experts avec quotes et navigation rapide vers les 3 départements.</p>
                  <p className="text-gray-700 mb-2"><strong>Priorité:</strong> <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm font-bold">HAUTE</span></p>
                  <p className="text-gray-700"><strong>Critères d'acceptation:</strong> Design attrayant, quotes inspirantes, cards départements cliquables</p>
                </div>

                <div className="border-l-4 border-indigo-500 pl-6">
                  <h5 className="font-bold text-indigo-900 mb-3 text-lg">RF-006: Modal Vue d'ensemble</h5>
                  <p className="text-gray-700 mb-2"><strong>Description:</strong> Bouton dans header ouvrant une modal documentant complètement PRIsME (vision, architecture, cybersécurité, IA, roadmap, etc.)</p>
                  <p className="text-gray-700 mb-2"><strong>Priorité:</strong> <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm font-bold">HAUTE</span></p>
                  <p className="text-gray-700"><strong>Critères d'acceptation:</strong> 10 onglets, design professionnel, contenu exhaustif</p>
                </div>
              </div>
            </div>

            {/* Exigences Non-Fonctionnelles */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-8 rounded-2xl border-2 border-purple-300 shadow-lg">
                <h4 className="text-2xl font-bold text-purple-900 mb-6 flex items-center gap-3">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Performance & Scalabilité
                </h4>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-purple-600 font-bold text-xl">✓</span>
                    <div>
                      <strong className="text-purple-900">Temps de chargement</strong>
                      <p className="text-sm text-purple-800">Page initiale &lt; 2s, navigation entre départements &lt; 500ms</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-600 font-bold text-xl">✓</span>
                    <div>
                      <strong className="text-purple-900">Responsive Design</strong>
                      <p className="text-sm text-purple-800">Support Desktop (1920px), Tablette (768px), Mobile (375px)</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-600 font-bold text-xl">✓</span>
                    <div>
                      <strong className="text-purple-900">Évolutivité</strong>
                      <p className="text-sm text-purple-800">Architecture permettant l'ajout facile de nouveaux départements</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-purple-600 font-bold text-xl">✓</span>
                    <div>
                      <strong className="text-purple-900">Optimisation</strong>
                      <p className="text-sm text-purple-800">Lazy loading, code splitting, images optimisées (WebP)</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 p-8 rounded-2xl border-2 border-green-300 shadow-lg">
                <h4 className="text-2xl font-bold text-green-900 mb-6 flex items-center gap-3">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Sécurité & Confidentialité
                </h4>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold text-xl">🔒</span>
                    <div>
                      <strong className="text-green-900">Authentification</strong>
                      <p className="text-sm text-green-800">Accès restreint aux employés BZ via SSO (Single Sign-On)</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold text-xl">🛡️</span>
                    <div>
                      <strong className="text-green-900">Protection XSS/CSRF</strong>
                      <p className="text-sm text-green-800">React protège automatiquement, validation stricte des entrées</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold text-xl">📜</span>
                    <div>
                      <strong className="text-green-900">Conformité PIPEDA</strong>
                      <p className="text-sm text-green-800">Aucune donnée personnelle sensible stockée ou exposée</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold text-xl">🔐</span>
                    <div>
                      <strong className="text-green-900">Données Statiques</strong>
                      <p className="text-sm text-green-800">Visions stockées localement (TypeScript), pas de BD externe</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contraintes et Restrictions */}
            <div className="bg-gradient-to-r from-red-50 to-orange-50 p-8 rounded-2xl border-2 border-red-300 shadow-lg">
              <h4 className="text-2xl font-bold text-red-900 mb-6 flex items-center gap-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                Contraintes et Restrictions Critiques
              </h4>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white p-6 rounded-xl border-2 border-red-200">
                  <h5 className="font-bold text-red-900 mb-4">🚫 Restrictions de Contenu</h5>
                  <ul className="space-y-3 text-red-800 text-sm">
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <div><strong>Confidentialité Client:</strong> Aucun nom de client, projet spécifique ou donnée financière dans les visions</div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <div><strong>Informations Sensibles:</strong> Pas de salaires, d'adresses, de données personnelles identifiables</div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <div><strong>Stratégies Confidentielles:</strong> Éviter les détails de stratégies commerciales non-publiques</div>
                    </li>
                  </ul>
                </div>
                <div className="bg-white p-6 rounded-xl border-2 border-orange-200">
                  <h5 className="font-bold text-orange-900 mb-4">⚠️ Règles de Gouvernance</h5>
                  <ul className="space-y-3 text-orange-800 text-sm">
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <div><strong>Approbation Exécutive:</strong> Toute modification de standard doit être validée par Dany CA/CE</div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <div><strong>Cohérence BZ:</strong> Les visions doivent refléter les valeurs et la culture BZ</div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span>•</span>
                      <div><strong>Révision Trimestrielle:</strong> Contenu révisé tous les 3 mois pour pertinence</div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Utilisateurs et Rôles */}
            <div className="bg-white p-8 rounded-2xl border-2 border-indigo-200 shadow-lg">
              <h4 className="text-2xl font-bold text-indigo-900 mb-6 flex items-center gap-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                Profils Utilisateurs et Cas d'Usage
              </h4>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border-2 border-blue-300">
                  <h5 className="font-bold text-blue-900 mb-3 text-lg">👨‍💼 Gestionnaire</h5>
                  <p className="text-sm text-blue-800 mb-4">Directeur TI, Directeur de Compte, Responsable RH</p>
                  <ul className="text-xs text-blue-700 space-y-2">
                    <li>• Consulte les 18 piliers de son département</li>
                    <li>• Lit les visions des 3 experts pour orientation stratégique</li>
                    <li>• Utilise PRIsME comme référence pour formations internes</li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border-2 border-purple-300">
                  <h5 className="font-bold text-purple-900 mb-3 text-lg">🎓 Nouvel Employé</h5>
                  <p className="text-sm text-purple-800 mb-4">Onboarding BZ - Première semaine</p>
                  <ul className="text-xs text-purple-700 space-y-2">
                    <li>• Découvre la culture et les valeurs BZ via Accueil</li>
                    <li>• Explore son département pour comprendre attentes</li>
                    <li>• Lit les visions de Dany pour absorber philosophie BZ</li>
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border-2 border-green-300">
                  <h5 className="font-bold text-green-900 mb-3 text-lg">⚙️ Administrateur</h5>
                  <p className="text-sm text-green-800 mb-4">Équipe technique BZ</p>
                  <ul className="text-xs text-green-700 space-y-2">
                    <li>• Modifie le contenu des tuiles (code source)</li>
                    <li>• Ajoute/met à jour les visions des experts</li>
                    <li>• Déploie les mises à jour après validation</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );

      case 'architecture':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Architecture Technique</h3>
              <p className="text-gray-600">Stack technologique moderne et évolutif</p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                <h4 className="text-lg font-semibold text-blue-900 mb-4">🎨 Frontend</h4>
                <ul className="space-y-2 text-blue-800">
                  <li className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-blue-100 px-2 py-1 rounded">React 18.3</span>
                    <span className="text-sm">+ TypeScript</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-blue-100 px-2 py-1 rounded">Vite</span>
                    <span className="text-sm">Build tool ultra-rapide</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-blue-100 px-2 py-1 rounded">Tailwind CSS</span>
                    <span className="text-sm">Design system cohérent</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-blue-100 px-2 py-1 rounded">Framer Motion</span>
                    <span className="text-sm">Animations fluides</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-blue-100 px-2 py-1 rounded">React Router</span>
                    <span className="text-sm">Navigation SPA</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
                <h4 className="text-lg font-semibold text-purple-900 mb-4">💾 Data Management</h4>
                <ul className="space-y-2 text-purple-800">
                  <li className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-purple-100 px-2 py-1 rounded">TypeScript Interfaces</span>
                    <span className="text-sm">Type safety</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-purple-100 px-2 py-1 rounded">Modular Data Files</span>
                    <span className="text-sm">aikupData, ventesData, adminRHData</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-purple-100 px-2 py-1 rounded">Component State</span>
                    <span className="text-sm">React hooks (useState, useEffect)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                <h4 className="text-lg font-semibold text-green-900 mb-4">🎯 Design Patterns</h4>
                <ul className="space-y-2 text-green-800">
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">Composants réutilisables (DRY principle)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">Modal system unifié (ExpertVisionModal)</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">Responsive design mobile-first</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">Animations performantes (CSS & Framer)</span>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-red-50 p-6 rounded-xl border border-orange-200">
                <h4 className="text-lg font-semibold text-orange-900 mb-4">🚀 Déploiement</h4>
                <ul className="space-y-2 text-orange-800">
                  <li className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-orange-100 px-2 py-1 rounded">Git Version Control</span>
                    <span className="text-sm">GitHub</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-orange-100 px-2 py-1 rounded">CI/CD Pipeline</span>
                    <span className="text-sm">Automated builds</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="font-mono text-sm bg-orange-100 px-2 py-1 rounded">Production Build</span>
                    <span className="text-sm">Optimized bundle</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-100 via-purple-100 to-pink-100 p-6 rounded-xl border border-blue-300">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">📐 Architecture des Composants</h4>
              <div className="space-y-2 text-gray-800 font-mono text-sm">
                <div>/ (Root)</div>
                <div className="ml-4">├── 🏠 Accueil (Landing page)</div>
                <div className="ml-4">├── 👥 Direction TI (18 tuiles)</div>
                <div className="ml-4">├── 📈 Directeur de Compte (18 tuiles)</div>
                <div className="ml-4">├── 🧑‍💼 Administration & RH (18 tuiles)</div>
                <div className="ml-4">├── 💡 Concepts (Frameworks)</div>
                <div className="ml-4">└── 🤖 Démo AI</div>
                <div className="mt-4">Composants Partagés:</div>
                <div className="ml-4">├── AikupCard (Tuiles interactives)</div>
                <div className="ml-4">├── ExpertAvatar (Dany, Dany, François)</div>
                <div className="ml-4">├── ExpertVisionModal (Visions détaillées)</div>
                <div className="ml-4">└── PRIsMEOverviewModal (Vue d'ensemble - Cette modal!)</div>
              </div>
            </div>
          </div>
        );

      case 'cybersecurity': {
        // Scores NIST 2.0 par catégorie (sur 5) - AUDIT RÉEL 2024
        const nistScores = {
          govern: 2.5,  // ⚠️ Credentials hardcodés, pas de backend auth
          identify: 3.5, // ✓ Bonne documentation mais failles identifiées
          protect: 2.0,  // 🚨 Clés API exposées, pas de CSP, pas de rate limiting
          detect: 1.5,   // 🔴 Aucun monitoring, pas de logs sécurisés
          respond: 2.0,  // ⚠️ Pas de playbooks, gestion erreurs trop verbeuse
          recover: 1.8   // 🔴 Pas de backup automatique, pas de plan continuité
        };
        
        const overallNistScore = ((nistScores.govern + nistScores.identify + nistScores.protect + nistScores.detect + nistScores.respond + nistScores.recover) / 6).toFixed(1);
        
        // Fonction pour générer le spider chart
        const generateSpiderChart = () => {
          const categories = [
            { name: 'GOVERN', score: nistScores.govern, angle: 0 },
            { name: 'IDENTIFY', score: nistScores.identify, angle: 60 },
            { name: 'PROTECT', score: nistScores.protect, angle: 120 },
            { name: 'DETECT', score: nistScores.detect, angle: 180 },
            { name: 'RESPOND', score: nistScores.respond, angle: 240 },
            { name: 'RECOVER', score: nistScores.recover, angle: 300 }
          ];
          
          const centerX = 200;
          const centerY = 200;
          const maxRadius = 150;
          
          // Calculer les points du polygone
          const points = categories.map(cat => {
            const angle = (cat.angle - 90) * (Math.PI / 180);
            const radius = (cat.score / 5) * maxRadius;
            return `${centerX + radius * Math.cos(angle)},${centerY + radius * Math.sin(angle)}`;
          }).join(' ');
          
          // Points pour les labels
          const labels = categories.map(cat => {
            const angle = (cat.angle - 90) * (Math.PI / 180);
            const labelRadius = maxRadius + 40;
            return {
              name: cat.name,
              score: cat.score,
              x: centerX + labelRadius * Math.cos(angle),
              y: centerY + labelRadius * Math.sin(angle)
            };
          });
          
          return (
            <svg width="400" height="400" viewBox="0 0 400 400" className="mx-auto">
              {/* Grilles concentriques */}
              {[1, 2, 3, 4, 5].map((level) => (
                <polygon
                  key={level}
                  points={categories.map(cat => {
                    const angle = (cat.angle - 90) * (Math.PI / 180);
                    const radius = (level / 5) * maxRadius;
                    return `${centerX + radius * Math.cos(angle)},${centerY + radius * Math.sin(angle)}`;
                  }).join(' ')}
                  fill="none"
                  stroke={level === 5 ? '#cbd5e1' : '#e2e8f0'}
                  strokeWidth={level === 5 ? '2' : '1'}
                  strokeDasharray={level === 5 ? '0' : '5,5'}
                />
              ))}
              
              {/* Axes */}
              {categories.map(cat => {
                const angle = (cat.angle - 90) * (Math.PI / 180);
                return (
                  <line
                    key={cat.name}
                    x1={centerX}
                    y1={centerY}
                    x2={centerX + maxRadius * Math.cos(angle)}
                    y2={centerY + maxRadius * Math.sin(angle)}
                    stroke="#cbd5e1"
                    strokeWidth="1"
                  />
                );
              })}
              
              {/* Polygone de score */}
              <polygon
                points={points}
                fill="rgba(59, 130, 246, 0.3)"
                stroke="#3b82f6"
                strokeWidth="3"
              />
              
              {/* Points de score */}
              {categories.map(cat => {
                const angle = (cat.angle - 90) * (Math.PI / 180);
                const radius = (cat.score / 5) * maxRadius;
                return (
                  <circle
                    key={cat.name}
                    cx={centerX + radius * Math.cos(angle)}
                    cy={centerY + radius * Math.sin(angle)}
                    r="6"
                    fill="#3b82f6"
                    stroke="white"
                    strokeWidth="2"
                  />
                );
              })}
              
              {/* Labels */}
              {labels.map(label => (
                <g key={label.name}>
                  <text
                    x={label.x}
                    y={label.y}
                    textAnchor="middle"
                    className="text-sm font-bold fill-gray-700"
                  >
                    {label.name}
                  </text>
                  <text
                    x={label.x}
                    y={label.y + 16}
                    textAnchor="middle"
                    className="text-xs font-semibold fill-blue-600"
                  >
                    {label.score.toFixed(1)}/5
                  </text>
                </g>
              ))}
            </svg>
          );
        };
        
        return (
          <div className="space-y-8">
            {/* Header PRO */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center animate-float shadow-xl ring-4 ring-blue-200">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">Cybersécurité NIST 2.0</h3>
              <p className="text-gray-600 text-lg">Évaluation selon le cadre NIST Cybersecurity Framework 2.0</p>
            </div>

            {/* Score Global NIST */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white p-8 rounded-3xl shadow-2xl border-4 border-blue-200">
              <div className="text-center">
                <div className="text-6xl font-black mb-2">{overallNistScore}</div>
                <div className="text-2xl font-bold mb-1">/ 5.0</div>
                <div className="text-lg text-blue-100">Score Global NIST Cybersecurity Framework 2.0</div>
                <div className="mt-4 inline-block px-6 py-2 bg-white/20 backdrop-blur-md rounded-full border border-white/30">
                  <span className="font-bold text-xl">⚠️ Niveau: EN DÉVELOPPEMENT - Failles Critiques Identifiées</span>
                </div>
              </div>
            </div>

            {/* Spider Chart */}
            <div className="bg-white p-8 rounded-3xl shadow-xl border-2 border-gray-200">
              <h4 className="text-2xl font-bold text-center text-gray-900 mb-6">📊 Vue Radar - 6 Fonctions NIST 2.0</h4>
              {generateSpiderChart()}
              <p className="text-center text-sm text-gray-500 mt-6">
                Le graphique radar visualise la maturité d'PRIsME sur les 6 fonctions clés du cadre NIST CSF 2.0
              </p>
            </div>

            {/* Scores par Catégorie - Cards */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* GOVERN */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border-2 border-purple-300 shadow-lg transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xl font-bold text-purple-900">GOVERN</h4>
                  <div className="text-3xl font-black text-purple-600">{nistScores.govern}/5</div>
                </div>
                <div className="w-full bg-purple-200 rounded-full h-3 mb-3">
                  <div className="bg-gradient-to-r from-purple-500 to-purple-700 h-3 rounded-full" style={{ width: `${(nistScores.govern / 5) * 100}%` }}></div>
                </div>
                <p className="text-sm text-purple-800">
                  Gouvernance, stratégie, politiques de cybersécurité, et gestion des risques organisationnels.
                </p>
              </div>

              {/* IDENTIFY */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border-2 border-blue-300 shadow-lg transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xl font-bold text-blue-900">IDENTIFY</h4>
                  <div className="text-3xl font-black text-blue-600">{nistScores.identify}/5</div>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-3 mb-3">
                  <div className="bg-gradient-to-r from-blue-500 to-blue-700 h-3 rounded-full" style={{ width: `${(nistScores.identify / 5) * 100}%` }}></div>
                </div>
                <p className="text-sm text-blue-800">
                  Inventaire des actifs, évaluation des risques, identification des vulnérabilités et des menaces.
                </p>
              </div>

              {/* PROTECT */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border-2 border-green-300 shadow-lg transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xl font-bold text-green-900">PROTECT</h4>
                  <div className="text-3xl font-black text-green-600">{nistScores.protect}/5</div>
                </div>
                <div className="w-full bg-green-200 rounded-full h-3 mb-3">
                  <div className="bg-gradient-to-r from-green-500 to-green-700 h-3 rounded-full" style={{ width: `${(nistScores.protect / 5) * 100}%` }}></div>
                </div>
                <p className="text-sm text-green-800">
                  Contrôles d'accès, formation, protection des données, maintenance préventive, et sauvegarde.
                </p>
              </div>

              {/* DETECT */}
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-2xl border-2 border-yellow-300 shadow-lg transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xl font-bold text-yellow-900">DETECT</h4>
                  <div className="text-3xl font-black text-yellow-600">{nistScores.detect}/5</div>
                </div>
                <div className="w-full bg-yellow-200 rounded-full h-3 mb-3">
                  <div className="bg-gradient-to-r from-yellow-500 to-yellow-700 h-3 rounded-full" style={{ width: `${(nistScores.detect / 5) * 100}%` }}></div>
                </div>
                <p className="text-sm text-yellow-800">
                  Surveillance continue, détection d'anomalies, monitoring des événements de sécurité.
                </p>
              </div>

              {/* RESPOND */}
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-2xl border-2 border-orange-300 shadow-lg transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xl font-bold text-orange-900">RESPOND</h4>
                  <div className="text-3xl font-black text-orange-600">{nistScores.respond}/5</div>
                </div>
                <div className="w-full bg-orange-200 rounded-full h-3 mb-3">
                  <div className="bg-gradient-to-r from-orange-500 to-orange-700 h-3 rounded-full" style={{ width: `${(nistScores.respond / 5) * 100}%` }}></div>
                </div>
                <p className="text-sm text-orange-800">
                  Plan de réponse aux incidents, communication, analyse post-incident, et mitigation.
                </p>
              </div>

              {/* RECOVER */}
              <div className="bg-gradient-to-br from-red-50 to-red-100 p-6 rounded-2xl border-2 border-red-300 shadow-lg transform hover:scale-105 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-xl font-bold text-red-900">RECOVER</h4>
                  <div className="text-3xl font-black text-red-600">{nistScores.recover}/5</div>
                </div>
                <div className="w-full bg-red-200 rounded-full h-3 mb-3">
                  <div className="bg-gradient-to-r from-red-500 to-red-700 h-3 rounded-full" style={{ width: `${(nistScores.recover / 5) * 100}%` }}></div>
                </div>
                <p className="text-sm text-red-800">
                  Plan de continuité, récupération des données, communication post-incident, leçons apprises.
                </p>
              </div>
            </div>

            {/* Points Forts & Failles Critiques */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl border-2 border-green-300 shadow-lg">
                <h4 className="text-2xl font-bold text-green-900 mb-4 flex items-center gap-2">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  💪 Points Forts
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold text-xl">✓</span>
                    <div>
                      <strong className="text-green-900">IDENTIFY (3.5/5):</strong>
                      <p className="text-green-800 text-sm">Documentation complète, architecture claire, code source bien organisé sur GitHub</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold text-xl">✓</span>
                    <div>
                      <strong className="text-green-900">Framework React:</strong>
                      <p className="text-green-800 text-sm">Protection native contre XSS, composants isolés, TypeScript pour sécurité du code</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-green-600 font-bold text-xl">✓</span>
                    <div>
                      <strong className="text-green-900">Git Audit Trail:</strong>
                      <p className="text-green-800 text-sm">Historique complet des modifications, contrôle de version, revue de code</p>
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-red-50 to-orange-50 p-8 rounded-2xl border-2 border-red-400 shadow-lg">
                <h4 className="text-2xl font-bold text-red-900 mb-4 flex items-center gap-2">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  🚨 FAILLES CRITIQUES (P0)
                </h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold text-xl">🔥</span>
                    <div>
                      <strong className="text-red-900">Clés API OpenAI exposées:</strong>
                      <p className="text-red-800 text-sm">2 clés hardcodées dans le code source (openaiRapportIncident.ts, AIDocumentEditor.tsx)</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold text-xl">🔥</span>
                    <div>
                      <strong className="text-red-900">Auth JWT côté client:</strong>
                      <p className="text-red-800 text-sm">Credentials hardcodés, pas de backend, facilement bypassable via localStorage</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold text-xl">⚠️</span>
                    <div>
                      <strong className="text-red-900">Pas de rate limiting:</strong>
                      <p className="text-red-800 text-sm">Appels OpenAI illimités = risque d'abus et coûts incontrôlés ($$$)</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold text-xl">⚠️</span>
                    <div>
                      <strong className="text-red-900">Pas de validation inputs:</strong>
                      <p className="text-red-800 text-sm">Vulnérable à prompt injection et génération de contenu malveillant</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Plan d'Action Sécurité - 6 Mois */}
            <div className="bg-gradient-to-r from-red-100 via-orange-100 to-yellow-100 p-8 rounded-2xl border-2 border-red-300">
              <h4 className="text-2xl font-bold text-red-900 mb-6 text-center">🚀 Plan d'Action Sécurité - 6 Prochains Mois</h4>
              
              {/* Phase 1: URGENCE */}
              <div className="mb-6">
                <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-lg shadow-md mb-3">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">🔥</span>
                    <h5 className="text-xl font-bold text-red-900">PHASE 1: URGENCE (Semaine 1-2)</h5>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-semibold text-red-800 mb-2">🚨 Actions Critiques:</p>
                      <ul className="text-sm space-y-1 text-red-700 ml-4">
                        <li>• Révoquer clés API OpenAI exposées</li>
                        <li>• Créer backend Express sécurisé</li>
                        <li>• Déplacer toutes clés côté serveur</li>
                        <li>• Implémenter rate limiting (10 req/15min)</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-red-800 mb-2">🛡️ Sécurisation Immédiate:</p>
                      <ul className="text-sm space-y-1 text-red-700 ml-4">
                        <li>• Créer .env.example (template sécurisé)</li>
                        <li>• Ajouter validation inputs (DOMPurify)</li>
                        <li>• Remplacer innerHTML par textContent</li>
                        <li>• Nettoyer logs sensibles console</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Phase 2: AUTHENTIFICATION */}
              <div className="mb-6">
                <div className="bg-orange-50 border-l-4 border-orange-600 p-6 rounded-lg shadow-md mb-3">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">🔐</span>
                    <h5 className="text-xl font-bold text-orange-900">PHASE 2: AUTHENTIFICATION (Semaine 3-6)</h5>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-semibold text-orange-800 mb-2">🎯 Backend Auth:</p>
                      <ul className="text-sm space-y-1 text-orange-700 ml-4">
                        <li>• JWT signé côté serveur (bcrypt)</li>
                        <li>• Refresh tokens (rotation automatique)</li>
                        <li>• Session management sécurisé</li>
                        <li>• Middleware auth sur toutes routes</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-orange-800 mb-2">🔒 Encryption:</p>
                      <ul className="text-sm space-y-1 text-orange-700 ml-4">
                        <li>• crypto-js pour localStorage</li>
                        <li>• Tokens encryptés AES-256</li>
                        <li>• HTTPS enforcement (production)</li>
                        <li>• Secure cookies (httpOnly, sameSite)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Phase 3: FRONTEND HARDENING */}
              <div className="mb-6">
                <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6 rounded-lg shadow-md mb-3">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">🛡️</span>
                    <h5 className="text-xl font-bold text-yellow-900">PHASE 3: FRONTEND HARDENING (Semaine 7-12)</h5>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-semibold text-yellow-800 mb-2">🔧 Headers Sécurité:</p>
                      <ul className="text-sm space-y-1 text-yellow-700 ml-4">
                        <li>• Content Security Policy (CSP)</li>
                        <li>• X-Frame-Options: DENY</li>
                        <li>• X-Content-Type-Options: nosniff</li>
                        <li>• Helmet.js pour Node backend</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-yellow-800 mb-2">✅ Validation Complète:</p>
                      <ul className="text-sm space-y-1 text-yellow-700 ml-4">
                        <li>• Zod/Yup pour schémas validation</li>
                        <li>• Sanitization tous formulaires</li>
                        <li>• Protection prompt injection OpenAI</li>
                        <li>• Gestion erreurs sécurisée (pas verbose)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Phase 4: MONITORING & INFRASTRUCTURE */}
              <div>
                <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded-lg shadow-md">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-2xl">📊</span>
                    <h5 className="text-xl font-bold text-green-900">PHASE 4: MONITORING & INFRA (Semaine 13-24)</h5>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-semibold text-green-800 mb-2">📈 Monitoring:</p>
                      <ul className="text-sm space-y-1 text-green-700 ml-4">
                        <li>• Winston pour logs structurés</li>
                        <li>• Sentry pour error tracking</li>
                        <li>• OpenAI usage tracking (coûts)</li>
                        <li>• Alertes anomalies temps réel</li>
                      </ul>
                    </div>
                    <div>
                      <p className="font-semibold text-green-800 mb-2">🔄 Continuité:</p>
                      <ul className="text-sm space-y-1 text-green-700 ml-4">
                        <li>• Backup automatique quotidien</li>
                        <li>• Plan récupération (RPO/RTO)</li>
                        <li>• Tests restore mensuels</li>
                        <li>• Audit npm (dependabot)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Objectif Final */}
              <div className="mt-6 bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-xl text-center">
                <p className="text-lg font-bold mb-2">🎯 OBJECTIF 6 MOIS</p>
                <p className="text-3xl font-black mb-2">Score NIST: 2.2 → 4.0+</p>
                <p className="text-sm opacity-90">Sécurité Production-Ready • Conforme SOC2 Type I • Prêt pour clients entreprise</p>
              </div>
            </div>
          </div>
        );
      }

      case 'ai-integration':
        return (
          <div className="space-y-8">
            {/* Header PRO */}
            <div className="text-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-full flex items-center justify-center animate-float shadow-xl ring-4 ring-purple-200">
                <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">Intelligence Artificielle Intégrée</h3>
              <p className="text-gray-600 text-lg">L'IA comme partenaire intelligent pour des décisions optimisées. Collaboration humain-machine éthique et sécurisée.</p>
            </div>

            {/* Vision BZ: IA Collaborative & Éthique */}
            <div className="bg-gradient-to-r from-purple-100 via-indigo-100 to-blue-100 p-8 rounded-3xl border-2 border-purple-300 shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <h4 className="text-2xl font-bold text-purple-900">Vision BZ: IA Collaborative & Éthique</h4>
              </div>
              <p className="text-lg text-purple-800 mb-6 text-center font-semibold">
                L'humain décide, l'IA optimise, ensemble nous excellons
              </p>
              
              {/* Métriques clés */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-white p-6 rounded-2xl text-center shadow-md border-2 border-purple-200">
                  <div className="text-5xl font-black text-purple-600 mb-2">100%</div>
                  <div className="text-sm font-bold text-purple-900">Décisions humaines</div>
                </div>
                <div className="bg-white p-6 rounded-2xl text-center shadow-md border-2 border-indigo-200">
                  <div className="text-5xl font-black text-indigo-600 mb-2">65%</div>
                  <div className="text-sm font-bold text-indigo-900">Efficacité accrue</div>
                </div>
                <div className="bg-white p-6 rounded-2xl text-center shadow-md border-2 border-blue-200">
                  <div className="text-5xl font-black text-blue-600 mb-2">0</div>
                  <div className="text-sm font-bold text-blue-900">Données exposées</div>
                </div>
              </div>
            </div>

            {/* Features IA - Cards Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Assistant Décisionnel */}
              <div className="bg-white p-6 rounded-2xl border-2 border-purple-200 shadow-lg hover:scale-105 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">Assistant Décisionnel</h4>
                </div>
                <p className="text-sm text-gray-700">
                  Boutons IA intégrés dans toutes les interfaces pour analyser les données et proposer des recommandations contextuelles
                </p>
              </div>

              {/* Développement Assisté */}
              <div className="bg-white p-6 rounded-2xl border-2 border-blue-200 shadow-lg hover:scale-105 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">Développement Assisté</h4>
                </div>
                <p className="text-sm text-gray-700">
                  Programmation collaborative avec IA pour backend et frontend. Code optimisé, tests automatiques, documentation générée
                </p>
              </div>

              {/* Analyse Prédictive */}
              <div className="bg-white p-6 rounded-2xl border-2 border-green-200 shadow-lg hover:scale-105 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">Analyse Prédictive</h4>
                </div>
                <p className="text-sm text-gray-700">
                  Modèles d'apprentissage pour identifier les tendances, prédire les performances et optimiser les stratégies
                </p>
              </div>

              {/* Sécurité Intelligente */}
              <div className="bg-white p-6 rounded-2xl border-2 border-indigo-200 shadow-lg hover:scale-105 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">Sécurité Intelligente</h4>
                </div>
                <p className="text-sm text-gray-700">
                  Détection d'anomalies en temps réel, renforcement automatique de la cybersécurité, protection des données sensibles
                </p>
              </div>

              {/* Optimisation Continue */}
              <div className="bg-white p-6 rounded-2xl border-2 border-yellow-200 shadow-lg hover:scale-105 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-yellow-700 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">Optimisation Continue</h4>
                </div>
                <p className="text-sm text-gray-700">
                  Machine learning pour améliorer les processus, réduire les délais et augmenter l'efficacité opérationnelle
                </p>
              </div>

              {/* Interface Conversationnelle */}
              <div className="bg-white p-6 rounded-2xl border-2 border-orange-200 shadow-lg hover:scale-105 transition-all duration-300">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-orange-700 rounded-lg flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-bold text-gray-900">Interface Conversationnelle</h4>
                </div>
                <p className="text-sm text-gray-700">
                  Chatbots intelligents pour le support utilisateur, formation interactive et assistance contextuelle 24/7
                </p>
              </div>
            </div>

            {/* Éthique & Protection + Avantages Concrets */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Éthique & Protection */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-8 rounded-2xl border-2 border-green-300 shadow-lg">
                <h4 className="text-2xl font-bold text-green-900 mb-6 flex items-center gap-2">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  Éthique & Protection
                </h4>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold text-xl">✓</span>
                    <div>
                      <strong className="text-green-900">Données Sensibles Protégées</strong>
                      <p className="text-sm text-green-800">Aucune donnée classifiée ou personnelle n'est transmise aux modèles IA externes</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold text-xl">✓</span>
                    <div>
                      <strong className="text-green-900">Transparence Totale</strong>
                      <p className="text-sm text-green-800">Chaque recommandation IA est expliquée et justifiée pour le décideur</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold text-xl">✓</span>
                    <div>
                      <strong className="text-green-900">Contrôle Humain</strong>
                      <p className="text-sm text-green-800">L'IA propose, l'humain dispose. Validation toujours requise</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-green-600 font-bold text-xl">✓</span>
                    <div>
                      <strong className="text-green-900">Audit Complet</strong>
                      <p className="text-sm text-green-800">Traçabilité de toutes les interactions IA pour conformité et amélioration</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Avantages Concrets */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-8 rounded-2xl border-2 border-blue-300 shadow-lg">
                <h4 className="text-2xl font-bold text-blue-900 mb-6 flex items-center gap-2">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Avantages Concrets
                </h4>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-blue-600 font-bold text-xl">→</span>
                    <div>
                      <strong className="text-blue-900">Décisions 65% plus rapides</strong>
                      <p className="text-sm text-blue-800">Grâce à l'analyse instantanée et recommandations contextuelles</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-600 font-bold text-xl">→</span>
                    <div>
                      <strong className="text-blue-900">Erreurs réduites de 80%</strong>
                      <p className="text-sm text-blue-800">Avec validation automatique et détection d'anomalies</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-600 font-bold text-xl">→</span>
                    <div>
                      <strong className="text-blue-900">Formation accélérée</strong>
                      <p className="text-sm text-blue-800">Pour nouveaux gestionnaires avec assistants IA personnalisés</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-blue-600 font-bold text-xl">→</span>
                    <div>
                      <strong className="text-blue-900">Innovation continue</strong>
                      <p className="text-sm text-blue-800">Grâce aux insights IA sur les tendances et opportunités</p>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            {/* Cadre NIST AI RMF */}
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-8 rounded-2xl border-2 border-indigo-300 shadow-lg">
              <h4 className="text-2xl font-bold text-indigo-900 mb-4 flex items-center gap-3">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Cadre NIST AI Risk Management Framework (AI RMF)
              </h4>
              <p className="text-indigo-800 mb-6">
                BZ Inc. applique le cadre NIST AI RMF pour garantir une utilisation responsable, sécurisée et éthique de l'intelligence artificielle.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white p-5 rounded-xl border-2 border-indigo-200">
                  <h5 className="font-bold text-indigo-900 mb-2 flex items-center gap-2">
                    <span className="text-2xl">🎯</span> GOVERN (Gouvernance)
                  </h5>
                  <p className="text-sm text-indigo-800">
                    Politiques claires, responsabilités définies, gouvernance IA intégrée à la stratégie BZ
                  </p>
                </div>

                <div className="bg-white p-5 rounded-xl border-2 border-purple-200">
                  <h5 className="font-bold text-purple-900 mb-2 flex items-center gap-2">
                    <span className="text-2xl">🔍</span> MAP (Cartographie)
                  </h5>
                  <p className="text-sm text-purple-800">
                    Inventaire complet des systèmes IA, identification des risques, contexte d'utilisation documenté
                  </p>
                </div>

                <div className="bg-white p-5 rounded-xl border-2 border-blue-200">
                  <h5 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                    <span className="text-2xl">📏</span> MEASURE (Mesure)
                  </h5>
                  <p className="text-sm text-blue-800">
                    Métriques de performance IA, évaluation continue des biais, tests de fiabilité réguliers
                  </p>
                </div>

                <div className="bg-white p-5 rounded-xl border-2 border-green-200">
                  <h5 className="font-bold text-green-900 mb-2 flex items-center gap-2">
                    <span className="text-2xl">🛡️</span> MANAGE (Gestion)
                  </h5>
                  <p className="text-sm text-green-800">
                    Plans d'action, mitigation des risques, amélioration continue, réponse aux incidents IA
                  </p>
                </div>
              </div>

              <div className="mt-6 bg-white p-5 rounded-xl border-2 border-indigo-300">
                <h5 className="font-bold text-indigo-900 mb-3 text-center">🏆 Principes Directeurs NIST AI RMF @ BZ</h5>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className="font-bold text-indigo-900">Sécurité & Résilience</div>
                    <div className="text-indigo-700">Protection contre menaces IA</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-indigo-900">Transparence & Explicabilité</div>
                    <div className="text-indigo-700">IA compréhensible et justifiable</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-indigo-900">Équité & Inclusion</div>
                    <div className="text-indigo-700">Élimination des biais discriminatoires</div>
                  </div>
                </div>
              </div>
            </div>

            {/* BZ Inc.: Pionniers de l'IA Éthique */}
            <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 text-white p-10 rounded-3xl shadow-2xl border-4 border-indigo-200">
              <div className="flex items-center justify-center gap-3 mb-6">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <h4 className="text-3xl font-bold">BZ Inc.: Pionniers de l'IA Éthique</h4>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white/20 backdrop-blur-md p-6 rounded-2xl text-center border border-white/30">
                  <div className="text-6xl font-black mb-2">2024</div>
                  <div className="font-bold text-lg">Première génération IA</div>
                  <div className="text-sm text-blue-100">dans toutes nos apps</div>
                </div>
                <div className="bg-white/20 backdrop-blur-md p-6 rounded-2xl text-center border border-white/30">
                  <div className="text-6xl font-black mb-2">100%</div>
                  <div className="font-bold text-lg">Clients satisfaits</div>
                  <div className="text-sm text-blue-100">Avec assistance IA</div>
                </div>
                <div className="bg-white/20 backdrop-blur-md p-6 rounded-2xl text-center border border-white/30">
                  <div className="text-6xl font-black mb-2">0</div>
                  <div className="font-bold text-lg">Incidents sécurité</div>
                  <div className="text-sm text-blue-100">Approche éthique</div>
                </div>
              </div>

              <div className="text-center bg-white/10 backdrop-blur-sm p-6 rounded-2xl border border-white/30">
                <p className="text-2xl font-bold italic mb-2">
                  "L'IA n'est pas là pour remplacer l'intelligence humaine, mais pour l'amplifier"
                </p>
                <p className="text-blue-100 font-semibold">- Vision BZ</p>
              </div>
            </div>
          </div>
        );

      case 'governance':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Gouvernance & Conformité</h3>
              <p className="text-gray-600">Standards de gouvernance pour maintenir l'excellence PRIsME</p>
            </div>

            <div className="grid gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border border-blue-200">
                <h4 className="text-xl font-semibold text-blue-900 mb-4">📋 Processus de Révision</h4>
                <ul className="space-y-3 text-blue-800">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">Q1:</span>
                    <div>
                      <strong>Révision Trimestrielle:</strong> Le Comité d'Excellence BZ se réunit pour revoir les standards 
                      de chaque département et s'assurer de leur pertinence.
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">Q2:</span>
                    <div>
                      <strong>Mise à Jour des Visions:</strong> Les 3 experts (Dany, Dany, François) ajoutent de nouvelles 
                      visions basées sur les apprentissages récents.
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">Q3:</span>
                    <div>
                      <strong>Feedback Employés:</strong> Intégration des suggestions d'amélioration des employés 
                      qui utilisent PRIsME au quotidien.
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">Q4:</span>
                    <div>
                      <strong>Bilan Annuel:</strong> Présentation des évolutions PRIsME au Dany CA/CE et planification 
                      stratégique pour l'année suivante.
                    </div>
                  </li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                <h4 className="text-xl font-semibold text-green-900 mb-4">👥 Rôles & Responsabilités</h4>
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg border border-green-200">
                    <h5 className="font-bold text-green-900 mb-2">🌐 PRIsME Master → Prismes Employés</h5>
                    <p className="text-green-800 text-sm">
                      Chaque employé possède son prisme personnel alimenté par PRIsME Master BZ + TactiCE (stratégie/tactique) + l’outil RH (dossier employé). Les mises à jour Master se propagent automatiquement vers les prismes employés.
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-green-200">
                    <h5 className="font-bold text-green-900 mb-2">🧭 Gouvernance Prisme Employé</h5>
                    <p className="text-green-800 text-sm">
                      Responsables de ligne / gestionnaires : propriétaires de leur équipe (création, revue, activation des accès). CE valide les standards, Dany garde le pouvoir de veto/alignement final.
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-green-200">
                    <h5 className="font-bold text-green-900 mb-2">🤝 Gouvernance Prisme Client</h5>
                    <p className="text-green-800 text-sm">
                      Le Prime est responsable du prisme de son client (création, personnalisation, mise à jour). Le CE vérifie la conformité, Dany valide les cas sensibles.
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-green-200">
                    <h5 className="font-bold text-green-900 mb-2">🧡 Dany Bourassa - Président</h5>
                    <p className="text-green-800 text-sm">
                      Responsable de la vision globale PRIsME. Rédaction des visions holistiques et authentiques. 
                      Validation finale de tous les contenus.
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-green-200">
                    <h5 className="font-bold text-green-900 mb-2">❤️ Dany - CA/CE</h5>
                    <p className="text-green-800 text-sm">
                      Focus sur l'angle développement d'affaires et commercial. Visions orientées croissance, 
                      clients et différenciation marché.
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-green-200">
                    <h5 className="font-bold text-green-900 mb-2">💙 François - VP Techno</h5>
                    <p className="text-green-800 text-sm">
                      Angle technique et innovation. Visions sur l'architecture, les outils, l'automatisation 
                      et l'excellence technologique.
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-green-200">
                    <h5 className="font-bold text-green-900 mb-2">👨‍💼 Gestionnaires de Département</h5>
                    <p className="text-green-800 text-sm">
                      Responsables de proposer des améliorations pour leur département respectif et de s'assurer 
                      que leur équipe utilise PRIsME comme référence.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl border border-amber-200">
                <h4 className="text-xl font-semibold text-amber-900 mb-4">📌 RACI — Gouvernance PRIsME (employés & clients)</h4>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm text-amber-900">
                    <thead>
                      <tr className="bg-amber-100 text-left">
                        <th className="px-3 py-2">Tâche</th>
                        <th className="px-3 py-2">Dany</th>
                        <th className="px-3 py-2">CE</th>
                        <th className="px-3 py-2">Gestionnaires</th>
                        <th className="px-3 py-2">Prime (client)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-amber-100">
                      <tr>
                        <td className="px-3 py-2 font-semibold">Créer prisme employé</td>
                        <td className="px-3 py-2">I</td>
                        <td className="px-3 py-2">C</td>
                        <td className="px-3 py-2">R/A</td>
                        <td className="px-3 py-2">-</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-semibold">Mettre à jour contenu (Master → employé)</td>
                        <td className="px-3 py-2">A</td>
                        <td className="px-3 py-2">R</td>
                        <td className="px-3 py-2">C</td>
                        <td className="px-3 py-2">-</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-semibold">Alimentation TactiCE / RH vers prisme employé</td>
                        <td className="px-3 py-2">I</td>
                        <td className="px-3 py-2">C</td>
                        <td className="px-3 py-2">R/A</td>
                        <td className="px-3 py-2">-</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-semibold">Créer / maintenir prisme client</td>
                        <td className="px-3 py-2">I</td>
                        <td className="px-3 py-2">C</td>
                        <td className="px-3 py-2">C</td>
                        <td className="px-3 py-2">R/A</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 font-semibold">Conformité / qualité prismes (employé & client)</td>
                        <td className="px-3 py-2">A</td>
                        <td className="px-3 py-2">R</td>
                        <td className="px-3 py-2">C</td>
                        <td className="px-3 py-2">I</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="text-xs text-amber-800 mt-3">
                  R = Responsible, A = Accountable, C = Consulted, I = Informed
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
                <h4 className="text-xl font-semibold text-purple-900 mb-4">📊 Métriques de Succès</h4>
                <ul className="space-y-3 text-purple-800">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">📈</span>
                    <div>
                      <strong>Adoption:</strong> 100% des nouveaux employés doivent explorer PRIsME dans leur première semaine
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">💬</span>
                    <div>
                      <strong>Engagement:</strong> Minimum 3 feedbacks par département par trimestre
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">⭐</span>
                    <div>
                      <strong>Qualité:</strong> Score de satisfaction employé &gt; 85% sur l'utilité d'PRIsME
                    </div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-600 font-bold">🎯</span>
                    <div>
                      <strong>Impact:</strong> Mesure de l'amélioration des standards BZ année après année
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        );

      case 'kpi':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Indicateurs KPI PRIsME</h3>
              <p className="text-gray-600">Métriques de performance et d'engagement de la plateforme</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-2xl border-2 border-blue-300 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-blue-500 rounded-xl">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-black text-blue-600">100%</div>
                    <div className="text-xs text-blue-700 font-semibold">↑ +15%</div>
                  </div>
                </div>
                <h4 className="font-bold text-blue-900 mb-2">Taux d'Adoption</h4>
                <p className="text-sm text-blue-800">Tous les nouveaux employés explorent PRIsME dans leur première semaine</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-2xl border-2 border-purple-300 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-purple-500 rounded-xl">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5" />
                    </svg>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-black text-purple-600">4.8/5</div>
                    <div className="text-xs text-purple-700 font-semibold">↑ +0.3</div>
                  </div>
                </div>
                <h4 className="font-bold text-purple-900 mb-2">Satisfaction Employés</h4>
                <p className="text-sm text-purple-800">Score moyen sur l'utilité et la clarté des standards PRIsME</p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-2xl border-2 border-green-300 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-green-500 rounded-xl">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-black text-green-600">3.2k</div>
                    <div className="text-xs text-green-700 font-semibold">↑ +580</div>
                  </div>
                </div>
                <h4 className="font-bold text-green-900 mb-2">Vues Mensuelles</h4>
                <p className="text-sm text-green-800">Nombre de consultations des visions d'experts par mois</p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-6 rounded-2xl border-2 border-orange-300 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-orange-500 rounded-xl">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                    </svg>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-black text-orange-600">47</div>
                    <div className="text-xs text-orange-700 font-semibold">↑ +12</div>
                  </div>
                </div>
                <h4 className="font-bold text-orange-900 mb-2">Feedbacks/Trimestre</h4>
                <p className="text-sm text-orange-800">Suggestions d'amélioration reçues des employés</p>
              </div>

              <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 p-6 rounded-2xl border-2 border-cyan-300 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-cyan-500 rounded-xl">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-black text-cyan-600">+28%</div>
                    <div className="text-xs text-cyan-700 font-semibold">YoY</div>
                  </div>
                </div>
                <h4 className="font-bold text-cyan-900 mb-2">Amélioration Standards</h4>
                <p className="text-sm text-cyan-800">Augmentation mesurable de l'application des standards BZ</p>
              </div>

              <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-6 rounded-2xl border-2 border-pink-300 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <div className="p-3 bg-pink-500 rounded-xl">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-black text-pink-600">5.2min</div>
                    <div className="text-xs text-pink-700 font-semibold">Avg</div>
                  </div>
                </div>
                <h4 className="font-bold text-pink-900 mb-2">Temps par Session</h4>
                <p className="text-sm text-pink-800">Durée moyenne passée sur PRIsME par visite</p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-100 via-purple-100 to-pink-100 p-6 rounded-xl border border-blue-300">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">📊 Objectifs Q1 2025</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-lg">1</div>
                  <div>
                    <div className="font-bold text-gray-900">Compléter toutes les visions</div>
                    <div className="text-sm text-gray-600">162 visions (54 tuiles × 3 experts)</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold text-lg">2</div>
                  <div>
                    <div className="font-bold text-gray-900">Atteindre 90% satisfaction</div>
                    <div className="text-sm text-gray-600">Score employés sur utilité PRIsME</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold text-lg">3</div>
                  <div>
                    <div className="font-bold text-gray-900">5,000 vues mensuelles</div>
                    <div className="text-sm text-gray-600">+56% vs. baseline actuel</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-lg">4</div>
                  <div>
                    <div className="font-bold text-gray-900">100 feedbacks/trimestre</div>
                    <div className="text-sm text-gray-600">Engagement actif des employés</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'roi':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">$ ROI & Payback PRIsME</h3>
              <p className="text-gray-600">Analyse financière et retour sur investissement</p>
            </div>

            {/* Chiffres Clés PRO */}
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white p-8 rounded-3xl shadow-2xl transform hover:scale-105 transition-all">
                <div className="text-6xl font-black mb-2">287%</div>
                <div className="text-xl font-bold mb-2">ROI Estimé</div>
                <div className="text-green-100 text-sm">Retour sur investissement sur 3 ans</div>
              </div>
              <div className="bg-gradient-to-br from-blue-600 to-cyan-700 text-white p-8 rounded-3xl shadow-2xl transform hover:scale-105 transition-all">
                <div className="text-6xl font-black mb-2">8</div>
                <div className="text-xl font-bold mb-2">Mois Payback</div>
                <div className="text-blue-100 text-sm">Récupération de l'investissement initial</div>
              </div>
              <div className="bg-gradient-to-br from-purple-600 to-pink-700 text-white p-8 rounded-3xl shadow-2xl transform hover:scale-105 transition-all">
                <div className="text-6xl font-black mb-2">$285k</div>
                <div className="text-xl font-bold mb-2">Économies/an</div>
                <div className="text-purple-100 text-sm">Gains d'efficacité et réduction erreurs</div>
              </div>
            </div>

            {/* Investissement Initial */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-6 rounded-2xl border border-gray-300">
              <h4 className="text-xl font-bold text-gray-900 mb-4">💰 Investissement Initial</h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-700">Développement Plateforme</span>
                    <span className="font-bold text-gray-900">$95,000</span>
                  </div>
                  <div className="text-xs text-gray-600">Design, développement, tests (6 mois)</div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-700">Création Contenu</span>
                    <span className="font-bold text-gray-900">$42,000</span>
                  </div>
                  <div className="text-xs text-gray-600">162 visions d'experts + documentation</div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-700">Formation & Déploiement</span>
                    <span className="font-bold text-gray-900">$18,000</span>
                  </div>
                  <div className="text-xs text-gray-600">Onboarding de tous les employés</div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-gray-700">Maintenance Annuelle</span>
                    <span className="font-bold text-gray-900">$24,000</span>
                  </div>
                  <div className="text-xs text-gray-600">Mises à jour et support continu</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t-2 border-gray-300">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-gray-900">TOTAL INVESTISSEMENT</span>
                  <span className="text-2xl font-black text-blue-600">$155,000</span>
                </div>
              </div>
            </div>

            {/* Bénéfices Annuels */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-2xl border border-green-300">
              <h4 className="text-xl font-bold text-green-900 mb-4">📈 Bénéfices Annuels Récurrents</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-white p-4 rounded-xl">
                  <div>
                    <div className="font-bold text-gray-900">Réduction du temps de formation</div>
                    <div className="text-sm text-gray-600">-65% de temps pour former nouveaux employés</div>
                  </div>
                  <div className="text-2xl font-bold text-green-600">$85,000</div>
                </div>
                <div className="flex items-center justify-between bg-white p-4 rounded-xl">
                  <div>
                    <div className="font-bold text-gray-900">Amélioration de l'efficacité</div>
                    <div className="text-sm text-gray-600">+28% d'application des standards BZ</div>
                  </div>
                  <div className="text-2xl font-bold text-green-600">$125,000</div>
                </div>
                <div className="flex items-center justify-between bg-white p-4 rounded-xl">
                  <div>
                    <div className="font-bold text-gray-900">Réduction des erreurs</div>
                    <div className="text-sm text-gray-600">-42% d'erreurs par meilleure compréhension</div>
                  </div>
                  <div className="text-2xl font-bold text-green-600">$55,000</div>
                </div>
                <div className="flex items-center justify-between bg-white p-4 rounded-xl">
                  <div>
                    <div className="font-bold text-gray-900">Rétention des talents</div>
                    <div className="text-sm text-gray-600">Meilleure compréhension = engagement accru</div>
                  </div>
                  <div className="text-2xl font-bold text-green-600">$20,000</div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t-2 border-green-400">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold text-green-900">TOTAL BÉNÉFICES/AN</span>
                  <span className="text-3xl font-black text-green-600">$285,000</span>
                </div>
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white p-6 rounded-2xl border-2 border-blue-300 shadow-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-6">📅 Timeline ROI</h4>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="w-32 font-bold text-gray-700">Année 1</div>
                  <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full" style={{width: '67%'}}></div>
                    <div className="absolute inset-0 flex items-center justify-center font-bold text-white">Payback: 8 mois</div>
                  </div>
                  <div className="w-32 text-right font-bold text-blue-600">+$130k</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-32 font-bold text-gray-700">Année 2</div>
                  <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full" style={{width: '100%'}}></div>
                    <div className="absolute inset-0 flex items-center justify-center font-bold text-white">Rentabilité complète</div>
                  </div>
                  <div className="w-32 text-right font-bold text-green-600">+$261k</div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-32 font-bold text-gray-700">Année 3</div>
                  <div className="flex-1 bg-gray-200 rounded-full h-8 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full" style={{width: '100%'}}></div>
                    <div className="absolute inset-0 flex items-center justify-center font-bold text-white">ROI 287%</div>
                  </div>
                  <div className="w-32 text-right font-bold text-purple-600">+$261k</div>
                </div>
              </div>
              <div className="mt-6 p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-300">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-gray-900">GAIN NET SUR 3 ANS:</span>
                  <span className="text-3xl font-black text-orange-600">$652,000</span>
                </div>
              </div>
            </div>
          </div>
        );

      case 'guide':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Guide Utilisateur PRIsME</h3>
              <p className="text-gray-600">Tout ce que vous devez savoir pour tirer le maximum d'PRIsME</p>
            </div>

            {/* Quick Start */}
            <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white p-8 rounded-3xl shadow-2xl">
              <h4 className="text-2xl font-bold mb-4">🚀 Quick Start - 5 minutes</h4>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-white/20 backdrop-blur-md p-4 rounded-xl">
                  <div className="text-3xl font-black mb-2">1</div>
                  <div className="font-bold mb-1">Choisis ton département</div>
                  <div className="text-sm text-blue-100">TI, Ventes ou Admin/RH</div>
                </div>
                <div className="bg-white/20 backdrop-blur-md p-4 rounded-xl">
                  <div className="text-3xl font-black mb-2">2</div>
                  <div className="font-bold mb-1">Explore les 18 tuiles</div>
                  <div className="text-sm text-blue-100">Clique sur chaque pilier</div>
                </div>
                <div className="bg-white/20 backdrop-blur-md p-4 rounded-xl">
                  <div className="text-3xl font-black mb-2">3</div>
                  <div className="font-bold mb-1">Lis les visions</div>
                  <div className="text-sm text-blue-100">Clique sur Dany 🧡 Dany ❤️ François 💙</div>
                </div>
                <div className="bg-white/20 backdrop-blur-md p-4 rounded-xl">
                  <div className="text-3xl font-black mb-2">4</div>
                  <div className="font-bold mb-1">Applique au quotidien</div>
                  <div className="text-sm text-blue-100">Intègre les standards BZ</div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white p-6 rounded-2xl border-2 border-blue-300 shadow-lg">
                <h4 className="text-lg font-bold text-blue-900 mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  Navigation dans PRIsME
                </h4>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <div><strong>Sidebar gauche:</strong> Accès rapide aux 3 départements + Concepts</div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <div><strong>Bouton Vue d'ensemble:</strong> Documentation complète (cette modal!)</div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <div><strong>Cartes tuiles:</strong> Clique pour ouvrir les détails</div>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-blue-600 font-bold">•</span>
                    <div><strong>Avatars experts:</strong> Clique pour lire leur vision spécifique</div>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-2xl border-2 border-purple-300 shadow-lg">
                <h4 className="text-lg font-bold text-purple-900 mb-4 flex items-center gap-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  Les 3 Experts
                </h4>
                <div className="space-y-2 text-sm text-purple-800">
                  <p className="font-semibold text-purple-900">Dany 🧡 — Vision stratégique & leadership</p>
                  <p className="font-semibold text-purple-900">Dany ❤️ — Développement d'affaires</p>
                  <p className="font-semibold text-purple-900">François 💙 — Excellence technique</p>
                </div>
              </div>
            </div>


            {/* FAQ Rapide */}
            <div className="bg-white p-6 rounded-2xl border-2 border-gray-300 shadow-lg">
              <h4 className="text-xl font-bold text-gray-900 mb-4">❓ Questions Fréquentes</h4>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4">
                  <div className="font-bold text-gray-900 mb-1">Combien de temps pour explorer un département?</div>
                  <div className="text-sm text-gray-600">15-20 minutes pour survoler les 18 tuiles. 2-3 heures pour tout lire en profondeur.</div>
                </div>
                <div className="border-l-4 border-purple-500 pl-4">
                  <div className="font-bold text-gray-900 mb-1">Dois-je tout mémoriser?</div>
                  <div className="text-sm text-gray-600">Non! PRIsME est une référence à consulter au besoin. L'important est de comprendre l'esprit des standards.</div>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <div className="font-bold text-gray-900 mb-1">Puis-je suggérer des améliorations?</div>
                  <div className="text-sm text-gray-600">Absolument! Toutes les suggestions sont bienvenues via le système de feedback. Le Comité d'Excellence les évalue.</div>
                </div>
                <div className="border-l-4 border-orange-500 pl-4">
                  <div className="font-bold text-gray-900 mb-1">PRIsME sera-t-il mis à jour?</div>
                  <div className="text-sm text-gray-600">Oui, révision trimestrielle par le Comité d'Excellence. Les visions évoluent avec l'apprentissage BZ.</div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'roadmap':
        return (
          <div className="space-y-6">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Roadmap & Vision 2024-2027</h3>
              <p className="text-gray-600">L'évolution d'PRIsME vers l'excellence organisationnelle totale</p>
            </div>

            <div className="space-y-4">
              {/* Phase 1 - 2024 Q4 */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border-l-4 border-green-500">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center text-white font-bold">
                    ✓
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-green-900">Phase 1 - Fondation (Q4 2024)</h4>
                    <p className="text-green-700 text-sm">COMPLÉTÉE ✅</p>
                  </div>
                </div>
                <ul className="space-y-2 text-green-800 ml-15">
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✓</span> Création des 3 départements (TI, Ventes, Admin/RH)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✓</span> 54 tuiles d'excellence (18 par département)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✓</span> Intégration des 3 experts avec avatars et visions
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✓</span> 12 visions complètes de Dany (Direction TI)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-green-600">✓</span> Modal Vue d'ensemble (cette modal!) selon standard BZ
                  </li>
                </ul>
              </div>

              {/* Phase 2 - 2025 Q1 */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-6 rounded-xl border-l-4 border-blue-500">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold">
                    2
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-blue-900">Phase 2 - Enrichissement (Q1 2025)</h4>
                    <p className="text-blue-700 text-sm">PROCHAINE ÉTAPE 🎯</p>
                  </div>
                </div>
                <ul className="space-y-2 text-blue-800 ml-15">
                  <li className="flex items-center gap-2">
                    <span className="text-blue-600">→</span> Compléter toutes les visions de Dany (36 visions supplémentaires)
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-600">→</span> Ajouter les visions de Dany pour tous les départements
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-600">→</span> Ajouter les visions de François pour tous les départements
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-600">→</span> Créer les vrais avatars pour Dany et François
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-blue-600">→</span> Ajout de modales spécialisées pour concepts clés
                  </li>
                </ul>
              </div>

              {/* Phase 3 - 2025 Q2-Q3 */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border-l-4 border-purple-500">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white font-bold">
                    3
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-purple-900">Phase 3 - Interactivité IA (Q2-Q3 2025)</h4>
                    <p className="text-purple-700 text-sm">INNOVATION 🚀</p>
                  </div>
                </div>
                <ul className="space-y-2 text-purple-800 ml-15">
                  <li className="flex items-center gap-2">
                    <span className="text-purple-600">⚡</span> Chatbot IA intégré : « Demande à Dany/Dany/François »
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-purple-600">⚡</span> Recommandations personnalisées par rôle/département
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-purple-600">⚡</span> Génération de plans d'action basés sur les tuiles
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-purple-600">⚡</span> Quiz interactifs pour valider la compréhension
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-purple-600">⚡</span> Analytics : tracking de l'engagement par tuile/expert
                  </li>
                </ul>
              </div>

              {/* Phase 4 - 2025 Q4 - 2026 */}
              <div className="bg-gradient-to-r from-orange-50 to-red-50 p-6 rounded-xl border-l-4 border-orange-500">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center text-white font-bold">
                    4
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-orange-900">Phase 4 - Expansion Client (Q4 2025 - 2026)</h4>
                    <p className="text-orange-700 text-sm">VISION STRATÉGIQUE 🎯</p>
                  </div>
                </div>
                <ul className="space-y-2 text-orange-800 ml-15">
                  <li className="flex items-center gap-2">
                    <span className="text-orange-600">🌟</span> PRIsME White-Label pour clients BZ
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-orange-600">🌟</span> Personnalisation des tuiles par organisation cliente
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-orange-600">🌟</span> Intégration avec TactiCE pour alignement stratégique
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-orange-600">🌟</span> Marketplace de concepts : partage entre organisations
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-orange-600">🌟</span> PRIsME devient un produit SaaS commercialisable
                  </li>
                </ul>
              </div>

              {/* Vision 2027 */}
              <div className="bg-gradient-to-r from-yellow-50 via-orange-50 to-red-50 p-8 rounded-2xl border-2 border-yellow-400 shadow-lg">
                <div className="text-center">
                  <h4 className="text-2xl font-bold text-gray-900 mb-4">🌟 Vision 2027 : PRIsME Universelle</h4>
                  <p className="text-lg text-gray-800 leading-relaxed max-w-3xl mx-auto">
                    PRIsME devient <strong>LA référence québécoise</strong> en excellence organisationnelle. 
                    Chaque organisation de taille moyenne à grande utilise PRIsME pour structurer, documenter 
                    et transmettre ses standards d'excellence. BZ Inc. est reconnu comme <strong>le leader 
                    en innovation de gestion holistique assistée par l'IA</strong>.
                  </p>
                  <div className="mt-6 flex justify-center gap-4">
                    <div className="bg-white px-6 py-3 rounded-full border-2 border-blue-300 font-bold text-blue-900">
                      500+ Organisations
                    </div>
                    <div className="bg-white px-6 py-3 rounded-full border-2 border-green-300 font-bold text-green-900">
                      10,000+ Utilisateurs
                    </div>
                    <div className="bg-white px-6 py-3 rounded-full border-2 border-purple-300 font-bold text-purple-900">
                      Leader Québec
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-100 via-purple-100 to-pink-100 p-6 rounded-xl border border-blue-300">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">💡 Principes Directeurs</h4>
              <ul className="grid md:grid-cols-2 gap-3 text-gray-800">
                <li className="flex items-center gap-2">
                  <span className="text-2xl">🧡</span>
                  <span><strong>Humain d'abord</strong> - L'IA assiste, ne remplace pas</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-2xl">⭐</span>
                  <span><strong>Excellence non-négociable</strong> - Standards élevés toujours</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-2xl">🤝</span>
                  <span><strong>Collaboration</strong> - 3 visions complémentaires</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-2xl">🔄</span>
                  <span><strong>Amélioration continue</strong> - Évolution constante</span>
                </li>
              </ul>
            </div>
          </div>
        );

      default:
        return <div>Contenu en développement...</div>;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal Container - FULLSCREEN PRO */}
      <div className="relative w-full max-w-[95vw] h-[95vh] bg-white rounded-3xl shadow-2xl overflow-hidden border-4 border-blue-500/20">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
              </div>
              <div>
                <h2 className="text-3xl font-bold">PRIsME - Vue d'ensemble</h2>
                <p className="text-blue-100 text-sm">Intelligence Augmentée pour l'Excellence Organisationnelle BZ</p>
                {lastAnalysisDate && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-purple-200">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                  </div>
                )}
              </div>
            </div>
            <button 
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all duration-200 hover:rotate-90"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Tabs - GRID PRO - 10 ONGLETS */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-300 px-6 py-4">
          <div className="grid grid-cols-5 gap-2 max-w-7xl mx-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all duration-300 whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 text-white shadow-xl scale-105 ring-4 ring-blue-200'
                    : 'bg-white text-gray-700 hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 border-2 border-gray-300 hover:border-blue-400 hover:shadow-md hover:scale-102'
                }`}
              >
                {tab.icon}
                <span className="text-sm font-bold">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Content - FULLSCREEN */}
        <div className="p-10 overflow-y-auto" style={{ height: 'calc(95vh - 220px)' }}>
          <div key={contentKey} className="animate-slide-in-right max-w-7xl mx-auto">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
}

